import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useUserShippingAddress } from "@hooks/use-addresses";
import Button from "@components/core/Button";
import Link from "@components/core/Link";
import { usePathname } from "@lib/i18n/navigation-app-router";
import { getExpressDeliveryDates } from "@utils/call-api/closets-call";
import { standardDeliveryDaysCount } from "@utils/deliveryDelays";
import ExpressDeliveryChoice from "./ExpressDeliveryChoice";

function ShippingAddressValidation({
  closetId,
  open,
  expressDeliverySummary,
  expressDeliverySelected,
  onExpressDeliveryChange,
}) {
  const t = useTranslations("common");

  const pathname = usePathname();
  const shippingAddress = useUserShippingAddress();

  const [deliveryDates, setDeliveryDates] = useState({
    standardDeliveryDate: null,
    expressDeliveryDate: null,
  });

  useEffect(() => {
    async function fetchDeliveryDates() {
      try {
        const res = await getExpressDeliveryDates(closetId);
        setDeliveryDates({
          standardDeliveryDate: res.standard_delivery_date,
          expressDeliveryDate: res.express_delivery_date,
        });
      } catch {
        // On failure, the static delay copy stays displayed
      }
    }

    if (open && expressDeliverySummary?.eligible) {
      fetchDeliveryDates();
    }
  }, [open, expressDeliverySummary?.eligible, closetId]);

  if (!shippingAddress) {
    return (
      <div className="p-3 bg-white rounded-sm">
        <Link
          href={{
            pathname: "/profile/addresses/new",
            query: {
              address_type: "delivery",
              redirect: pathname,
              origin: "closetDrawer",
            },
          }}
        >
          <Button fullWidth>{t("drawer.modal_rtc.validate.addresses.add_address")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2 p-3 bg-white rounded-sm">
      <div className="d-flex justify-content-between align-items-center">
        <p className="font-weight-bold font-size-sm text-uppercase mb-0">
          {shippingAddress.delivery_mode === "home"
            ? t("drawer.modal_rtc.validate.addresses.shipping_home")
            : t("drawer.modal_rtc.validate.addresses.shipping_relay")}
        </p>

        <div>
          <Link
            className="link-primary no-underline"
            href={{
              pathname: "/profile/addresses/shipping",
              query: {
                redirect: pathname,
                origin: "closetDrawer",
              },
            }}
          >
            {t("drawer.modal_rtc.validate.change")}
          </Link>
        </div>
      </div>

      <div>
        <p className="mb-0">
          {shippingAddress.delivery_mode === "home"
            ? `${shippingAddress.first_name} ${shippingAddress.last_name}`
            : shippingAddress.info}
        </p>
        <p className="mb-0">{shippingAddress.line1}</p>
        {shippingAddress.line2 && <p className="mb-0">{shippingAddress.line2}</p>}
        <p className="mb-0">
          {`${shippingAddress.zip} ${shippingAddress.city}, ${shippingAddress.country}`}
        </p>
      </div>

      {expressDeliverySummary?.eligible ? (
        <ExpressDeliveryChoice
          shippingCountry={shippingAddress.country}
          priceInCents={expressDeliverySummary.price}
          standardDeliveryDate={deliveryDates.standardDeliveryDate}
          expressDeliveryDate={deliveryDates.expressDeliveryDate}
          selected={expressDeliverySelected}
          onSelect={onExpressDeliveryChange}
        />
      ) : (
        <p className="font-size-xs text-gray mb-0">
          {t("drawer.modal_rtc.validate.addresses.preparation_delay", {
            daysCount: standardDeliveryDaysCount(shippingAddress.country),
          })}
        </p>
      )}
    </div>
  );
}

export default ShippingAddressValidation;
